import React, { useState, useCallback } from 'react';
import { Plus, ChevronLeft, ChevronRight, ArrowUpDown, ClipboardList } from 'lucide-react';
import PollCard from './PollCard';
import Tooltip from '../ui/Tooltip';
import styles from './PollsSidebar.module.css';

export default function PollsSidebar({
    activities,
    activeActivityId,
    launchedActivityId,
    isSidebarOpen,
    isSidebarCollapsed,
    openDropdownId,
    deletingId,
    isLaunchingId,
    activityMeta, // { [type]: { label, icon, color, bg } }
    onAddClick,
    onCollapseToggle,
    onSelectActivity,
    onKeyDown,
    onToggleDropdown,
    onCopyDirectLink,
    onResetResults,
    onDuplicate,
    onDelete,
    onToggleResults,
    onToggleCorrectAnswer,
    onToggleLock,
    onLaunch,
    onStop,
    onBackdropClick,
    onReorderActivities,
    eventExpired = false,
    eventExpiredMessage,
}) {
    const metaMap = activityMeta || {};
    const totalCount = activities.length;
    const liveCount = launchedActivityId != null ? 1 : 0;
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    const handleDragStart = useCallback((e, activityId) => {
        setDraggedId(activityId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(activityId));
        e.dataTransfer.setData('application/json', JSON.stringify({ activityId }));
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedId(null);
        setDragOverId(null);
    }, []);

    const handleDragOver = useCallback((e, activityId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (activityId !== draggedId) setDragOverId(activityId);
    }, [draggedId]);

    const handleDragLeave = useCallback(() => {
        setDragOverId(null);
    }, []);

    const handleDrop = useCallback(
        (e, dropTargetId) => {
            e.preventDefault();
            setDragOverId(null);
            setDraggedId(null);
            let sourceId = draggedId;
            if (sourceId == null) {
                try {
                    const raw = e.dataTransfer.getData('application/json');
                    if (raw) sourceId = JSON.parse(raw)?.activityId;
                } catch (_) {}
            }
            if (sourceId == null || Number(sourceId) === Number(dropTargetId) || !onReorderActivities) return;
            const fromIdx = activities.findIndex((a) => Number(a.id) === Number(sourceId));
            const toIdx = activities.findIndex((a) => Number(a.id) === Number(dropTargetId));
            if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
            const next = [...activities];
            const [removed] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, removed);
            onReorderActivities(next);
        },
        [activities, draggedId, onReorderActivities]
    );

    return (
        <>
            {isSidebarOpen && <div className={styles.sidebarBackdrop} onClick={onBackdropClick} aria-hidden />}
            <aside
                className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}
                aria-label="Interactions"
            >
                <div className={styles.sidebarHeader}>
                    <div className={styles.sidebarTitle}>Interactions</div>
                    <div className={styles.sidebarActions}>
                        <Tooltip title="Add interaction" placement="right">
                            <button
                                type="button"
                                className={`${styles.addBtn} ${isSidebarCollapsed ? styles.addBtnCompact : ''}`}
                                onClick={onAddClick}
                                aria-label="Add interaction"
                            >
                                <Plus size={14} />
                                <span className={styles.addBtnText}>Add</span>
                            </button>
                        </Tooltip>
                        <Tooltip title={isSidebarCollapsed ? 'Expand panel' : 'Collapse panel'} placement="right">
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={onCollapseToggle}
                                aria-pressed={isSidebarCollapsed}
                                aria-label={isSidebarCollapsed ? 'Expand interactions panel' : 'Collapse interactions panel'}
                            >
                                {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                            </button>
                        </Tooltip>
                    </div>
                </div>

                <div className={styles.listArea}>
                    {!isSidebarCollapsed && (
                        <div className={styles.groupBar}>
                            <span className={styles.groupTitle}>Polls</span>
                            <div className={styles.groupStats}>
                                <span className={styles.statChip}>{totalCount} total</span>
                                {liveCount > 0 && (
                                    <span className={`${styles.statChip} ${styles.statChipLive}`}>
                                        <span className={styles.statChipDot} />
                                        Live
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {!isSidebarCollapsed && totalCount > 1 && (
                        <p className={styles.reorderHint}>Drag to reorder run sequence</p>
                    )}

                    {activities.map((activity) => (
                        <div
                            key={activity.id}
                            className={`${styles.activityItemWrap} ${draggedId === activity.id ? styles.activityItemDragging : ''} ${dragOverId === activity.id ? styles.activityItemDragOver : ''}`}
                            onDragOver={(e) => handleDragOver(e, activity.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, activity.id)}
                        >
                            {!isSidebarCollapsed && onReorderActivities && (() => {
                                const handle = (
                                    <div
                                        className={styles.dragHandle}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, activity.id)}
                                        onDragEnd={handleDragEnd}
                                        aria-label="Drag to reorder"
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <ArrowUpDown size={14} />
                                    </div>
                                );
                                // This tooltip's z-index (999999) is higher than any poll
                                // card's own dropdown menu — if the mouse passes over a drag
                                // handle while reaching for an open dropdown's items further
                                // down the list, it would otherwise render on top of them.
                                return openDropdownId != null
                                    ? handle
                                    : <Tooltip title="Drag to reorder" placement="right">{handle}</Tooltip>;
                            })()}
                            <div className={styles.activityItemInner}>
                                <PollCard
                                    isSidebarCollapsed={isSidebarCollapsed}
                                    activity={activity}
                                    meta={metaMap[activity.type] || {}}
                                    isActive={activeActivityId === activity.id}
                                    isLaunched={launchedActivityId === activity.id}
                                    openDropdownId={openDropdownId}
                                    deletingId={deletingId}
                                    isLaunchingId={isLaunchingId}
                                    onSelect={onSelectActivity}
                                    onKeyDown={onKeyDown}
                                    onToggleDropdown={onToggleDropdown}
                                    onCopyDirectLink={onCopyDirectLink}
                                    onResetResults={onResetResults}
                                    onDuplicate={onDuplicate}
                                    onDelete={onDelete}
                                    onToggleResults={onToggleResults}
                                    onToggleCorrectAnswer={onToggleCorrectAnswer}
                                    onToggleLock={onToggleLock}
                                    onLaunch={onLaunch}
                                    onStop={onStop}
                                    eventExpired={eventExpired}
                                    eventExpiredMessage={eventExpiredMessage}
                                />
                            </div>
                        </div>
                    ))}

                    {activities.length === 0 && (
                        <div className={styles.sidebarEmpty}>
                            <ClipboardList size={22} className={styles.sidebarEmptyIcon} aria-hidden />
                            <span className={styles.sidebarEmptyText}>No interactions yet. Click Add to create your first poll.</span>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
